<script setup lang="ts">
import { EButton, EDropdownMenu, ETooltip, type MenuItem } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { currentLocale, isAppLocale, LOCALE_NAMES, setLocale, SUPPORTED_LOCALES } from '@/i18n'

/**
 * The language switch (§6, §11).
 *
 * Locale names are endonyms and are never translated — a picker that offers
 * "Dhivehi" to a Dhivehi speaker is a picker they cannot read. Choosing one
 * changes the catalogue, `<html lang>`, `<html dir>` and the remembered
 * preference in a single call, so the four can never disagree.
 */
const { locale } = useI18n()

const items = computed<MenuItem[]>(() =>
  SUPPORTED_LOCALES.map((value) => ({
    id: value,
    label: LOCALE_NAMES[value],
    ...(value === locale.value ? { icon: 'check' as const } : {}),
  })),
)

const label = computed(() => LOCALE_NAMES[currentLocale()])

function onSelect(item: MenuItem): void {
  if (isAppLocale(item.id) && item.id !== locale.value) setLocale(item.id)
}
</script>

<template>
  <EDropdownMenu :items="items" :label="$t('shell.locale.label')" @select="onSelect">
    <template #trigger>
      <ETooltip :content="$t('shell.locale.current', { language: label })">
        <EButton variant="ghost" size="sm" :aria-label="$t('shell.locale.trigger')">
          <span class="max-w-24 truncate">{{ label }}</span>
        </EButton>
      </ETooltip>
    </template>
  </EDropdownMenu>
</template>
